import React from 'react';
import { Box, Skeleton, Stack, TableCell, TableRow, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EXPLORER_ACCENT_SEQUENCE } from './utils';

/** Matches MUI Chip size="small" in ranking Marcas column. */
export const MARCAS_CHIP_SKELETON_WIDTH = 36;
export const MARCAS_CHIP_SKELETON_HEIGHT = 24;

export const MarcasChipSkeleton: React.FC<{ align?: 'left' | 'right' | 'center' }> = ({
    align = 'right',
}) => (
    <Skeleton
        variant="rounded"
        width={MARCAS_CHIP_SKELETON_WIDTH}
        height={MARCAS_CHIP_SKELETON_HEIGHT}
        sx={{
            borderRadius: '16px',
            ...(align === 'right' ? { ml: 'auto' } : align === 'center' ? { mx: 'auto' } : {}),
        }}
    />
);

/** Shared min-height so summary cards do not shift when Marcas count loads. */
export const SUMMARY_CARD_PAPER_SX = {
    p: 3,
    textAlign: 'center' as const,
    width: '100%',
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
};

/** Inner padding for Explorer list/table panels (matches global MuiList theme). */
export const EXPLORER_PANEL_CONTENT_SX = {
    pt: 1.5,
    pb: 1.5,
    pl: 2,
    pr: 2,
} as const;

/** Reset global MuiList chrome when nested inside Paper (matches summary/table panels). */
export const EXPLORER_PANEL_LIST_SX = {
    border: 'none',
    boxShadow: 'none',
    bgcolor: 'transparent',
    ...EXPLORER_PANEL_CONTENT_SX,
} as const;

/** h3-sized value placeholder. Avoid nesting Skeleton in Typography (overlaps the wave). */
export const SummaryValueSkeleton: React.FC = () => (
    <Skeleton
        variant="rounded"
        animation="wave"
        width={72}
        height={28}
        sx={{ mx: 'auto', maxWidth: '80%', borderRadius: 1 }}
    />
);

/** Dual metric placeholder (views + interactions / peak + avg). */
export const SummaryMetricPairSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2.5} justifyContent="center" alignItems="flex-start">
        <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                animation="wave"
                width={56}
                height={28}
                sx={{ mx: 'auto', borderRadius: 1 }}
            />
            <Skeleton
                variant="rounded"
                animation="wave"
                width={48}
                height={12}
                sx={{ mx: 'auto', mt: 0.75, borderRadius: 0.5 }}
            />
        </Box>
        <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                animation="wave"
                width={56}
                height={28}
                sx={{ mx: 'auto', borderRadius: 1 }}
            />
            <Skeleton
                variant="rounded"
                animation="wave"
                width={48}
                height={12}
                sx={{ mx: 'auto', mt: 0.75, borderRadius: 0.5 }}
            />
        </Box>
    </Stack>
);

export const RankingProgramRowSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="75%" height={22} sx={{ maxWidth: 200 }} />
            <Skeleton variant="text" width="55%" height={18} sx={{ maxWidth: 140 }} />
        </Box>
    </Stack>
);

/** Matches plain EntityImage ranking rows. Pass `bordered` for rating CompetitionEntityAvatar. */
export const RankingCanalesRowSkeleton: React.FC<{ bordered?: boolean }> = ({
    bordered = false,
}) => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton
            variant="rounded"
            width={40}
            height={40}
            sx={{
                borderRadius: 1,
                flexShrink: 0,
                boxSizing: 'border-box',
                ...(bordered
                    ? { border: '1px solid', borderColor: 'divider' }
                    : {}),
            }}
        />
        <Skeleton variant="text" width="70%" height={22} sx={{ maxWidth: 220 }} />
    </Stack>
);

/** Social/clips ranking name cell: plain 40×40 EntityImage + single-line name. */
export const RankingSocialEntityRowSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1, flexShrink: 0 }} />
        <Skeleton variant="text" width="70%" height={22} sx={{ maxWidth: 220 }} />
    </Stack>
);

/** Matches FavoriteButton IconButton size="small" hit target. */
export const RankingFavoriteCellSkeleton: React.FC = () => (
    <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1, mx: 'auto' }} />
);

/** Competition rating stream title: title + optional live chip + caption line. */
export const CompetitionStreamTitleSkeleton: React.FC = () => (
    <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Skeleton variant="text" width="70%" height={22} sx={{ maxWidth: 280, flex: 1, minWidth: 0 }} />
            <Skeleton variant="rounded" width={40} height={22} sx={{ borderRadius: '16px', flexShrink: 0 }} />
        </Stack>
        <Skeleton variant="text" width="45%" height={16} sx={{ maxWidth: 160, mt: 0.25 }} />
    </Box>
);

/** Competition / livestream detail action IconButtons. */
export const RankingActionsCellSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
    <Stack direction="row" justifyContent="flex-end" spacing={0}>
        {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} variant="circular" width={28} height={28} />
        ))}
    </Stack>
);

/** Social brand detail clips table row. */
export const SocialBrandClipRowSkeleton: React.FC = () => (
    <TableRow>
        <TableCell>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
                <Skeleton variant="text" width={120} height={20} />
            </Stack>
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={72} height={20} />
        </TableCell>
        <TableCell>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width="85%" height={22} sx={{ maxWidth: 240 }} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={88} height={20} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton variant="circular" width={28} height={28} sx={{ ml: 'auto' }} />
        </TableCell>
    </TableRow>
);

/** Social channel detail posts table row (platform, date, title, kind, ownership, metrics, open). */
export const SocialChannelPostRowSkeleton: React.FC = () => (
    <TableRow>
        <TableCell>
            <Skeleton variant="text" width={88} height={20} />
        </TableCell>
        <TableCell>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width="85%" height={22} sx={{ maxWidth: 280 }} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={56} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={72} height={20} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton variant="circular" width={28} height={28} sx={{ ml: 'auto' }} />
        </TableCell>
    </TableRow>
);

/** Channel detail programs table — avatar + single program name, 8 columns. */
export const PodcastDetailProgramRowSkeleton: React.FC = () => (
    <TableRow>
        <TableCell align="center">
            <Skeleton width={24} height={20} />
        </TableCell>
        <TableCell>
            <RankingCanalesRowSkeleton />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <MarcasChipSkeleton />
        </TableCell>
    </TableRow>
);

/** Program detail — Auspiciadores row (32px avatar, text, mention chip, expand). */
export const ProgramDetailBrandRowSkeleton: React.FC = () => (
    <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
            py: 1,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="50%" height={20} sx={{ maxWidth: 200 }} />
            <Skeleton variant="text" width="35%" height={16} sx={{ maxWidth: 120, mt: 0.5 }} />
        </Box>
        <Skeleton variant="rounded" width={88} height={24} sx={{ borderRadius: '16px', flexShrink: 0 }} />
        <Skeleton variant="circular" width={28} height={28} />
    </Stack>
);

export const LIVESTREAM_DETAIL_TABLE_MIN_WIDTH = 720;

/** A minmax(0, 1fr) grid has zero min-content width, so the cell never grows to fit a long title. */
export const LIVESTREAM_DETAIL_TITLE_WRAP_SX = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
} as const;

export type LivestreamDetailColKey =
    | 'fecha'
    | 'title'
    | 'peak'
    | 'duration'
    | 'avg'
    | 'views'
    | 'actions';

/**
 * Auto table layout: every column shrinks to its content; title takes the rest.
 * Wrap title content in LIVESTREAM_DETAIL_TITLE_WRAP_SX so long titles truncate instead of widening the table.
 */
export function livestreamDetailColSx(key: LivestreamDetailColKey) {
    switch (key) {
        case 'title':
            return { width: '100%', minWidth: 200 } as const;
        case 'fecha':
            return { width: 124, minWidth: 124, whiteSpace: 'nowrap' } as const;
        case 'actions':
            return { width: '1%', whiteSpace: 'nowrap', px: 1 } as const;
        default:
            return { width: '1%', whiteSpace: 'nowrap' } as const;
    }
}

/** Livestream detail table — fecha + title (left), metrics + actions (right). */
export const LivestreamDetailTableRowSkeleton: React.FC<{
    showSubtitleChip?: boolean;
}> = ({ showSubtitleChip = true }) => (
    <TableRow>
        <TableCell align="left" sx={livestreamDetailColSx('fecha')}>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell align="left" sx={livestreamDetailColSx('title')}>
            <Box sx={{ minWidth: 0 }}>
                <Skeleton variant="text" width="92%" height={22} sx={{ mb: showSubtitleChip ? 0.5 : 0 }} />
                {showSubtitleChip ? (
                    <Skeleton variant="rounded" width={88} height={22} sx={{ borderRadius: '16px' }} />
                ) : null}
            </Box>
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('views')}>
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('avg')}>
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('peak')}>
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('duration')}>
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('actions')}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </Stack>
        </TableCell>
    </TableRow>
);

/**
 * Episode table row skeleton.
 * Program detail: date, title, duration, views, likes, comments, actions.
 * Podcast detail: same plus a program column after title (`showProgram`).
 */
export const ProgramDetailEpisodeTableRowSkeleton: React.FC<{
    showProgram?: boolean;
}> = ({ showProgram = false }) => (
    <TableRow>
        <TableCell align="left" sx={{ width: 108 }}>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell align="left">
            <Skeleton variant="text" width="85%" height={22} sx={{ maxWidth: 320 }} />
        </TableCell>
        {showProgram ? (
            <TableCell align="left" sx={{ minWidth: 180 }}>
                <Skeleton variant="text" width={140} height={20} />
            </TableCell>
        ) : null}
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={{ width: 88, px: 0.5 }}>
            <RankingActionsCellSkeleton count={2} />
        </TableCell>
    </TableRow>
);

/** Program detail — Episodio row (title + metadata line, no thumbnail). */
export const ProgramDetailEpisodeRowSkeleton: React.FC = () => (
    <Box
        sx={{
            py: 1.5,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Skeleton variant="text" width="78%" height={20} sx={{ maxWidth: 420, mb: 0.75 }} />
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={48} height={16} />
            <Skeleton variant="text" width={56} height={16} />
        </Stack>
    </Box>
);

const COMPETITION_CHART_SKELETON_HEIGHT = 440;
const COMPETITION_CHART_Y_AXIS_WIDTH = 52;
const COMPETITION_SCHEDULE_LABEL_WIDTH = 152;
const COMPETITION_CHART_MARGIN_LEFT =
    COMPETITION_SCHEDULE_LABEL_WIDTH - COMPETITION_CHART_Y_AXIS_WIDTH;
const COMPETITION_CHART_MARGIN_RIGHT = 12;
const COMPETITION_SCHEDULE_ROW_PX = 32;

/** Deterministic wavy paths that read as concurrent-viewer lines (viewBox 0 0 100 60). */
const COMPETITION_SKELETON_LINES: ReadonlyArray<{ d: string; accent: number }> = [
    {
        accent: 0,
        d: 'M0 42 C8 40, 12 28, 18 30 S28 38, 34 32 S44 18, 52 22 S64 36, 72 28 S86 12, 100 16',
    },
    {
        accent: 1,
        d: 'M0 48 C10 46, 14 40, 22 42 S34 50, 42 44 S54 30, 62 34 S76 46, 84 40 S94 34, 100 36',
    },
    {
        accent: 2,
        d: 'M0 35 C9 33, 15 24, 24 26 S36 34, 44 28 S56 16, 66 20 S78 30, 88 24 S96 18, 100 20',
    },
    {
        accent: 3,
        d: 'M0 52 C8 50, 16 48, 24 49 S40 54, 48 50 S60 42, 70 45 S82 52, 90 48 S96 46, 100 47',
    },
    {
        accent: 4,
        d: 'M0 28 C7 26, 14 20, 22 22 S34 30, 42 24 S52 12, 62 15 S74 26, 82 20 S92 10, 100 12',
    },
];

const COMPETITION_SKELETON_SCHEDULE: ReadonlyArray<{
    barStart: string;
    barWidth: string;
}> = [
    { barStart: '8%', barWidth: '42%' },
    { barStart: '18%', barWidth: '55%' },
    { barStart: '4%', barWidth: '36%' },
    { barStart: '28%', barWidth: '48%' },
];

/** Rating / competencia chart placeholder: axes, grid, wavy lines, schedule strip. */
export const CompetitionChartSkeleton: React.FC<{ fullscreen?: boolean }> = ({
    fullscreen = false,
}) => {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const gridStroke = alpha(theme.palette.divider, dark ? 0.9 : 1);
    const lineOpacity = dark ? 0.45 : 0.38;

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                ...(fullscreen ? { minHeight: 0 } : {}),
            }}
            aria-hidden
        >
            {/* Matches data-competition-chart: fixed CHART_HEIGHT, axes inside the box. */}
            <Box
                sx={{
                    width: '100%',
                    height: fullscreen ? 0 : COMPETITION_CHART_SKELETON_HEIGHT,
                    minHeight: fullscreen ? 0 : COMPETITION_CHART_SKELETON_HEIGHT,
                    flex: fullscreen ? 1 : undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    pl: `${COMPETITION_CHART_MARGIN_LEFT}px`,
                    pr: `${COMPETITION_CHART_MARGIN_RIGHT}px`,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        pt: 1,
                        pb: 0.5,
                    }}
                >
                    <Stack
                        justifyContent="space-between"
                        sx={{
                            width: COMPETITION_CHART_Y_AXIS_WIDTH,
                            flexShrink: 0,
                            py: 0.25,
                            pr: 0.5,
                            alignItems: 'flex-end',
                        }}
                    >
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton
                                key={`y-${i}`}
                                variant="rounded"
                                animation="wave"
                                width={i === 4 ? 28 : 36}
                                height={10}
                                sx={{ borderRadius: 0.5 }}
                            />
                        ))}
                    </Stack>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            position: 'relative',
                        }}
                    >
                        <Box
                            component="svg"
                            viewBox="0 0 100 60"
                            preserveAspectRatio="none"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                overflow: 'visible',
                                '@keyframes competitionChartLinePulse': {
                                    '0%, 100%': { opacity: lineOpacity },
                                    '50%': { opacity: lineOpacity * 0.45 },
                                },
                            }}
                        >
                            {[12, 24, 36, 48].map((y) => (
                                <line
                                    key={`grid-${y}`}
                                    x1="0"
                                    y1={y}
                                    x2="100"
                                    y2={y}
                                    stroke={gridStroke}
                                    strokeWidth={0.35}
                                    strokeDasharray="1.2 1.2"
                                    vectorEffect="non-scaling-stroke"
                                />
                            ))}
                            {COMPETITION_SKELETON_LINES.map((line, index) => (
                                <path
                                    key={`line-${index}`}
                                    d={line.d}
                                    fill="none"
                                    stroke={EXPLORER_ACCENT_SEQUENCE[line.accent]}
                                    strokeWidth={1.75}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                    style={{
                                        animation: `competitionChartLinePulse 1.6s ease-in-out ${
                                            index * 0.12
                                        }s infinite`,
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        flexShrink: 0,
                        height: 28,
                        pl: `${COMPETITION_CHART_Y_AXIS_WIDTH}px`,
                        alignItems: 'center',
                    }}
                >
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton
                            key={`x-${i}`}
                            variant="rounded"
                            animation="wave"
                            width={36}
                            height={10}
                            sx={{ borderRadius: 0.5 }}
                        />
                    ))}
                </Stack>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    maxHeight: fullscreen ? 280 : 160,
                    overflowY: 'hidden',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    px: 0,
                    py: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                }}
            >
                {COMPETITION_SKELETON_SCHEDULE.map((row, index) => (
                    <Stack
                        key={`sched-${index}`}
                        direction="row"
                        alignItems="center"
                        sx={{
                            height: COMPETITION_SCHEDULE_ROW_PX,
                            minHeight: COMPETITION_SCHEDULE_ROW_PX,
                            borderRadius: 1,
                            bgcolor: alpha(
                                EXPLORER_ACCENT_SEQUENCE[index % EXPLORER_ACCENT_SEQUENCE.length]!,
                                dark ? 0.16 : 0.1,
                            ),
                            overflow: 'hidden',
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{
                                width: COMPETITION_SCHEDULE_LABEL_WIDTH,
                                flexShrink: 0,
                                px: 0.75,
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                width={20}
                                height={20}
                                sx={{
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                width={72 + (index % 3) * 12}
                                height={12}
                                sx={{ borderRadius: 0.5 }}
                            />
                        </Stack>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                position: 'relative',
                                height: '100%',
                                mr: `${COMPETITION_CHART_MARGIN_RIGHT}px`,
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                height={22}
                                sx={{
                                    position: 'absolute',
                                    left: row.barStart,
                                    width: row.barWidth,
                                    top: 5,
                                    borderRadius: 1,
                                    bgcolor: alpha(
                                        EXPLORER_ACCENT_SEQUENCE[
                                            index % EXPLORER_ACCENT_SEQUENCE.length
                                        ]!,
                                        dark ? 0.35 : 0.28,
                                    ),
                                }}
                            />
                        </Box>
                    </Stack>
                ))}
            </Box>
        </Box>
    );
};
